/**
 * Update Subscribed Values
 * Accepts device data from the graphql query and initializes all values according to
 * preferred units of measure and setpoint limit profiles.
 * Returns the object containing all formatted values for additional processing.
 **/
import {
  CurrentAttributes,
  Maybe,
  MyThermostat,
  SetpointProfile,
} from '../types';
import {
  convertToTempUnits,
  convertLimitTemperature,
} from '../../../util/convert-temp-units';
import {
  configureBaseSetpointLimits,
  LimitProfileAssignments,
} from './configure-base-setpoints';
import { usesCoreCentidegreeTargets } from './thermostat-control-edit';
import { TemperatureUnit } from '../../../../../system/models/temperatureUnits';
import {
  getThermostatObservedAt,
  usesObservationEvidence,
} from './observation-freshness';

export function updateSubscribedValues(
  device: MyThermostat,
  preferredUnits: TemperatureUnit,
  unitSetpointProfile?: Maybe<SetpointProfile>,
  propertySetpointProfile?: Maybe<SetpointProfile>,
  assignments?: LimitProfileAssignments,
): CurrentAttributes {
  let initialValues: CurrentAttributes = {
    setpoint:
      device.isMitsubishiThermostat === true
        ? convertLimitTemperature(device.setpoint?.value, preferredUnits)
        : convertToTempUnits(device.setpoint?.value, preferredUnits, 'C', 2),
    minSetpoint: usesCoreCentidegreeTargets(device)
      ? convertLimitTemperature(device.minSetpoint?.value, preferredUnits)
      : convertToTempUnits(device.minSetpoint?.value, preferredUnits, 'C', 2),
    maxSetpoint: usesCoreCentidegreeTargets(device)
      ? convertLimitTemperature(device.maxSetpoint?.value, preferredUnits)
      : convertToTempUnits(device.maxSetpoint?.value, preferredUnits, 'C', 2),
    supportsAutoMode: device.supportsAutoMode ?? true,
    supportsSingleSetpointAutoMode:
      device.supportsSingleSetpointAutoMode ?? false,
    supportsDualSetpointAutoMode: device.supportsDualSetpointAutoMode ?? true,
    deviceId: device.deviceId,
    name: device.name,
    setpointLimitBasis: '',
    baseMaxCoolSetpointLimit: undefined,
    baseMaxHeatSetpointLimit: undefined,
    baseMinCoolSetpointLimit: undefined,
    baseMinHeatSetpointLimit: undefined,
    followingSchedule: undefined,
    maxCoolSetpointLimit: convertLimitTemperature(
      device.maxCoolSetpointLimit?.value,
      preferredUnits,
    ),
    maxHeatSetpointLimit: convertLimitTemperature(
      device.maxHeatSetpointLimit?.value,
      preferredUnits,
    ),
    minCoolSetpointLimit: convertLimitTemperature(
      device.minCoolSetpointLimit?.value,
      preferredUnits,
    ),
    minHeatSetpointLimit: convertLimitTemperature(
      device.minHeatSetpointLimit?.value,
      preferredUnits,
    ),
    temperature: convertToTempUnits(
      device.meta?.display?.temperature ?? device.temperature?.value,
      preferredUnits,
    ),
    remoteSensorTempDelta: convertToTempUnits(
      device.remoteSensorTempDelta?.value,
      preferredUnits,
    ),
    humidity: device.meta?.display?.humidity ?? device.humidity?.value,
    heatSetpoint: device.isMitsubishiThermostat
      ? convertLimitTemperature(device.heatSetpoint?.value, preferredUnits)
      : convertToTempUnits(device.heatSetpoint?.value, preferredUnits),
    coolSetpoint: device.isMitsubishiThermostat
      ? convertLimitTemperature(device.coolSetpoint?.value, preferredUnits)
      : convertToTempUnits(device.coolSetpoint?.value, preferredUnits),

    fanModeInfo: device.fanModeInfo,
    fanMode: device.fanMode?.value,
    fanState: device.fanState?.value,
    fanSpeed: device.fanSpeed?.value,
    airDirection: device.louvreControlV1?.airDirection?.value,
    getFanState: device.getFanState,
    getHoldEnd: device.getHoldEnd,
    getSetpointChangeSource: device.getSetpointChangeSource,
    hasPendingUpdates: device.hasPendingUpdates,
    holdDuration: device.holdDuration?.value,
    holdEnd: device.holdEnd?.value,
    holdState: device.holdState?.value,
    isMultiSpeedThermostat: device.isMultiSpeedThermostat,
    keypadLockoutLevel: device.keypadLockoutLevel?.value,
    lastScheduleChange: device.lastScheduleChange?.value,
    lastSetpointChange: device.lastSetpointChange?.value,
    lastSetpointChangeValid: device.lastSetpointChangeValid,
    modbusRTU: device.modbusRTU,
    keypadLockoutLevels: device.keypadLockoutLevels ?? [],
    operatingMode: device.operatingMode?.value,
    operatingState: device.operatingState?.value,
    progMode: device.progMode?.value,
    passCode: device.passCode?.value,
    scheduleStatus: device.scheduleStatus,
    schedVisibilityMode: device.schedVisibilityMode?.value,
    setpointChangeSource: device.setpointChangeSource?.value,
    showFanButton: device.showFanButton?.value,
    sourceId: device.sourceId,
    supportsShowFanButton: device.supportsShowFanButton,
    supportsPasscode: device.supportsPasscode,
    supportsSystemType: device.supportsSystemType,
    supportsRemoteSensorTempDelta: device.supportsRemoteSensorTempDelta,
    supportsLastSetpointChange: device.supportsLastSetpointChange,
    supportsFanOperations: device.supportsFanOperations,
    supportsHoldExpiryTime: device.supportsHoldExpiryTime,
    systemType: device.modbusRTU ? 'SmartAHU' : device.systemType?.value,
    timestamp: device.timestamp,
    observedAt: usesObservationEvidence(device)
      ? getThermostatObservedAt(device)
      : undefined,
    tempDisplayMode: device.tempDisplayMode?.value,
    uiOptions: device.uiOptions?.value,
    useCustomSchedule: device.useCustomSchedule,
    useCustomSetpointLimits: device.useCustomSetpointLimits,
    zoneId: device.zoneId,
  };

  if (device.separatePowerControlV1) {
    initialValues.fanSpeed = device.separatePowerControlV1.fanSpeed?.value;
    initialValues.airDirection =
      device.separatePowerControlV1.airDirection?.value;
    initialValues.powerMode = device.separatePowerControlV1.powerMode?.value;
    initialValues.powerState = device.separatePowerControlV1.powerState?.value;
    initialValues.operatingMode =
      device.separatePowerControlV1.operatingMode?.value;
    initialValues.operatingState =
      device.separatePowerControlV1.operatingState?.value;
  }

  return configureBaseSetpointLimits(
    initialValues,
    preferredUnits,
    unitSetpointProfile,
    propertySetpointProfile,
    device,
    assignments,
  );
}
